// src/components/common/DashboardContainer.js
import React, { useState, useEffect } from 'react';
import { Box, Typography, CircularProgress, Paper } from '@mui/material';
import { useAppData } from '../../contexts/AppDataContext';

/**
 * A container component that handles loading states and displays a 
 * consistent loading experience across dashboard pages.
 */
const DashboardContainer = ({ 
  children, 
  title = "Loading Dashboard Data",
  loadingKeys = ['summary', 'hosts', 'clients', 'systemMetrics', 'clientLogs'],
  onRefresh
}) => {
  const { loading, forceRefreshAllData } = useAppData();
  const [isInitialLoad, setIsInitialLoad] = useState(true);
  
  // Check if any of the specified data is still loading
  const isLoading = loadingKeys.some(key => loading[key]);
  
  // Determine if this is the initial page load or a subsequent update
  useEffect(() => {
    if (!isLoading && isInitialLoad) {
      setIsInitialLoad(false);
    }
  }, [isLoading, isInitialLoad]);
  
  // Always render the children content, even during initial loading
  return (
    <Box>
      {/* Small loading indicator for data loads and refreshes */}
      {isLoading && (
        <Paper 
          elevation={3}
          sx={{ 
            position: 'fixed', 
            top: 16, 
            right: 16, 
            zIndex: 9999,
            display: 'flex',
            alignItems: 'center',
            borderRadius: 2,
            py: 1,
            px: 2,
          }}
        >
          <CircularProgress size={20} thickness={4} sx={{ mr: 1 }} />
          <Typography variant="body2">
            {isInitialLoad ? "Loading data..." : "Updating data..."}
          </Typography>
        </Paper>
      )}
      
      {/* Children content - always render */}
      {children}
    </Box>
  );
};

export default DashboardContainer;